import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, PackageX, ShieldAlert, Truck } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import {
  fetchOpenActions,
  fetchLowStock,
  fetchBlockedRoutes,
  fetchRecentOrders,
  formatFcfa,
  type DjaymaAction,
} from "@/lib/djayma";

// ---------------------------------------------------------------------------
// DJAYMA Control Tower (Epic 5.1) — live view over the djayma engine:
// intelligence actions, low stock, cash-variance blocked routes, recent orders.
// Reads via PostgREST/RLS; resilient to an empty or not-yet-migrated backend.
// ---------------------------------------------------------------------------

const ACTION_LABELS: Record<DjaymaAction["kind"], string> = {
  stockout_alert: "Alerte rupture",
  reorder_nudge: "Relance commande",
  churn_task: "Risque de churn",
  event_promo: "Promo événement",
};

function Kpi({
  icon,
  label,
  value,
  tone,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string | number;
  readonly tone: string;
}) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>{icon}</div>
        <div>
          <p className="text-2xl font-semibold text-stone-900">{value}</p>
          <p className="text-sm text-stone-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function ControlTower() {
  const actions = useQuery({ queryKey: ["djayma", "actions"], queryFn: fetchOpenActions, retry: false });
  const lowStock = useQuery({ queryKey: ["djayma", "low-stock"], queryFn: fetchLowStock, retry: false });
  const blocked = useQuery({ queryKey: ["djayma", "blocked-routes"], queryFn: fetchBlockedRoutes, retry: false });
  const orders = useQuery({ queryKey: ["djayma", "recent-orders"], queryFn: fetchRecentOrders, retry: false });

  const unreachable = actions.isError && lowStock.isError && blocked.isError && orders.isError;
  const actionsByKind = (actions.data ?? []).reduce<Record<string, number>>((acc, a) => {
    acc[a.kind] = (acc[a.kind] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Activity className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">Tour de contrôle DJAYMA</h1>
            <p className="text-sm text-stone-500">
              Moteur B2B — actions, stock, encaissement, commandes en temps réel
            </p>
          </div>
        </div>

        {unreachable && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Moteur DJAYMA non joignable — vérifiez que les migrations 131-137 sont appliquées et que le schéma
            <code className="mx-1 rounded bg-amber-100 px-1">djayma</code> est exposé à l&apos;API.
          </div>
        )}

        {/* KPI row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi icon={<AlertTriangle className="h-5 w-5" />} tone="bg-amber-100 text-amber-700"
               label="Actions ouvertes" value={actions.data?.length ?? 0} />
          <Kpi icon={<PackageX className="h-5 w-5" />} tone="bg-red-100 text-red-700"
               label="SKU sous seuil" value={lowStock.data?.length ?? 0} />
          <Kpi icon={<ShieldAlert className="h-5 w-5" />} tone="bg-rose-100 text-rose-700"
               label="Tournées bloquées (caisse)" value={blocked.data?.length ?? 0} />
          <Kpi icon={<Truck className="h-5 w-5" />} tone="bg-emerald-100 text-emerald-700"
               label="Commandes récentes" value={orders.data?.length ?? 0} />
        </div>

        {/* Actions by kind */}
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-stone-700">Files d&apos;actions</h2>
          {(actions.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-stone-400">Aucune action ouverte.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {Object.entries(actionsByKind).map(([kind, n]) => (
                <span key={kind} className="rounded-full bg-stone-100 px-3 py-1 text-sm text-stone-700">
                  {ACTION_LABELS[kind as DjaymaAction["kind"]] ?? kind}: <strong>{n}</strong>
                </span>
              ))}
            </div>
          )}
        </section>

        {/* Blocked routes (Andon) */}
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-stone-700">Tournées bloquées (écart de caisse)</h2>
          {(blocked.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-stone-400">Aucune tournée bloquée. ✅</p>
          ) : (
            <ul className="divide-y divide-stone-100 text-sm">
              {blocked.data?.map((r) => (
                <li key={r.id} className="flex items-center justify-between py-2">
                  <span className="text-stone-700">{r.zone} · {r.vehicle_ref}</span>
                  <span className="text-stone-400">{r.route_date}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Recent orders */}
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-stone-700">Commandes récentes</h2>
          {(orders.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-stone-400">Aucune commande.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-stone-400">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Statut</th>
                    <th className="py-2 pr-4 font-medium">Canal</th>
                    <th className="py-2 pr-4 font-medium">Paiement</th>
                    <th className="py-2 pr-4 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {orders.data?.map((o) => (
                    <tr key={o.id}>
                      <td className="py-2 pr-4 text-stone-700">{o.status}</td>
                      <td className="py-2 pr-4 text-stone-500">{o.channel}</td>
                      <td className="py-2 pr-4 text-stone-500">{o.payment_status}</td>
                      <td className="py-2 pr-4 text-right font-medium text-stone-800">{formatFcfa(o.total_xof)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
